import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ClientesService } from '../../../core/services/clientes.service';
import { UbigeoService, UbigeoItem } from '../../../core/services/ubigeo.service';
import { Cliente, ClienteKpis } from '../../../shared/models/system';

@Component({
  selector: 'app-clientes-admin',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    SelectModule,
    ToastModule,
    ConfirmDialogModule,
    TooltipModule
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './clientes-admin.html',
  styleUrls: ['./clientes-admin.css']
})
export class ClientesAdminComponent implements OnInit {
  clientes: Cliente[] = [];
  cliente: Partial<Cliente> = {};
  kpis: ClienteKpis = { total: 0, sinCorreo: 0, sinCuentaWeb: 0, invitacionesEnviadas: 0 };
  loading = true;
  first = 0;
  rows = 10;
  totalRecords = 0;
  search = '';
  private searchTimer: ReturnType<typeof setTimeout> | null = null;
  dialog: boolean = false;
  submitted: boolean = false;
  inviting = false;

  paises: UbigeoItem[] = [];
  departamentos: UbigeoItem[] = [];
  provincias: UbigeoItem[] = [];
  distritos: UbigeoItem[] = [];
  paisId: number | null = null;
  departamentoId: number | null = null;
  provinciaId: number | null = null;

  private readonly emailPattern = /^[A-Za-z0-9+_.-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

  constructor(
    private service: ClientesService,
    private ubigeo: UbigeoService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.loadKpis();
    this.ubigeo.getPaises().subscribe({
      next: (p) => { this.paises = p; this.cdr.detectChanges(); }
    });
  }

  scheduleServerLoad(): void {
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.reloadFirst(), 350);
  }

  reloadFirst(): void {
    this.first = 0;
    this.load({ first: 0, rows: this.rows });
  }

  load(event?: TableLazyLoadEvent) {
    this.loading = true;
    const first = event?.first ?? this.first;
    const size = event?.rows || this.rows;
    this.first = first || 0;
    this.rows = size;
    const page = this.rows > 0 ? Math.floor(this.first / this.rows) : 0;
    this.service.getAll(page, this.rows, this.search).subscribe({
      next: (data) => {
        this.clientes = data?.content || [];
        this.totalRecords = data?.totalElements || 0;
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.clientes = [];
        this.totalRecords = 0;
        this.loading = false;
        this.cdr.detectChanges();
      }
    });
  }

  loadKpis() {
    this.service.getKpis().subscribe({
      next: (k) => {
        this.kpis = k;
        this.cdr.detectChanges();
      }
    });
  }

  private replaceCliente(saved: Cliente) {
    const index = this.clientes.findIndex(item => item.id === saved.id);
    if (index < 0) {
      this.load({ first: this.first, rows: this.rows });
      return;
    }
    const next = this.clientes.slice();
    next[index] = { ...this.clientes[index], ...saved };
    this.clientes = next;
    this.cdr.detectChanges();
  }

  openNew() {
    this.cliente = { isActive: true };
    this.resetUbigeo();
    this.submitted = false;
    this.dialog = true;
  }

  edit(item: Cliente) {
    this.cliente = { ...item };
    this.submitted = false;
    this.dialog = true;
    this.loadUbigeoFromCliente(item);
  }

  private resetUbigeo() {
    this.paisId = null;
    this.departamentoId = null;
    this.provinciaId = null;
    this.departamentos = [];
    this.provincias = [];
    this.distritos = [];
  }

  private loadUbigeoFromCliente(item: Cliente) {
    this.resetUbigeo();
    if (!item.distritoId) return;
    this.ubigeo.getDistritoConPadres(item.distritoId).subscribe({
      next: (padres) => {
        this.paisId = padres.paisId;
        this.departamentoId = padres.departamentoId;
        this.provinciaId = padres.provinciaId;
        this.cliente.distritoId = item.distritoId;
        this.ubigeo.getDepartamentos(padres.paisId).subscribe({
          next: (d) => {
            this.departamentos = d;
            this.ubigeo.getProvincias(padres.departamentoId).subscribe({
              next: (p) => {
                this.provincias = p;
                this.ubigeo.getDistritos(padres.provinciaId).subscribe({
                  next: (dis) => {
                    this.distritos = dis;
                    this.cdr.detectChanges();
                  }
                });
              }
            });
          }
        });
      }
    });
  }

  onPaisChange() {
    this.departamentoId = null;
    this.provinciaId = null;
    this.cliente.distritoId = null;
    this.provincias = [];
    this.distritos = [];
    if (this.paisId) {
      this.ubigeo.getDepartamentos(this.paisId).subscribe({
        next: (d) => { this.departamentos = d; this.cdr.detectChanges(); }
      });
    } else this.departamentos = [];
  }

  onDepartamentoChange() {
    this.provinciaId = null;
    this.cliente.distritoId = null;
    this.distritos = [];
    if (this.departamentoId) {
      this.ubigeo.getProvincias(this.departamentoId).subscribe({
        next: (p) => { this.provincias = p; this.cdr.detectChanges(); }
      });
    } else this.provincias = [];
  }

  onProvinciaChange() {
    this.cliente.distritoId = null;
    if (this.provinciaId) {
      this.ubigeo.getDistritos(this.provinciaId).subscribe({
        next: (d) => { this.distritos = d; this.cdr.detectChanges(); }
      });
    } else this.distritos = [];
  }

  isValidEmail(value?: string | null): boolean {
    if (!value || !value.trim()) return true;
    return this.emailPattern.test(value.trim());
  }

  save() {
    this.submitted = true;
    if (!this.cliente.nombreCliente?.trim() || !this.cliente.celular?.trim()) return;
    if (!this.isValidEmail(this.cliente.email)) return;

    const payload: Partial<Cliente> = {
      nombreCliente: this.cliente.nombreCliente.trim(),
      celular: this.cliente.celular.trim(),
      email: this.cliente.email?.trim() || undefined,
      dni: this.cliente.dni?.trim() || undefined,
      distritoId: this.cliente.distritoId || undefined,
      direccion: this.cliente.direccion?.trim() || undefined
    };

    const req = this.cliente.id
      ? this.service.update(this.cliente.id, payload)
      : this.service.create(payload);

    req.subscribe({
      next: (saved) => {
        this.messageService.add({ severity: 'success', summary: 'Éxito', detail: 'Cliente guardado' });
        this.dialog = false;
        if (this.cliente.id && saved?.id) this.replaceCliente(saved);
        else this.reloadFirst();
        this.loadKpis();
      },
      error: (err) => {
        const msg = err.error?.message || 'No se pudo guardar el cliente.';
        this.messageService.add({ severity: 'error', summary: 'Error', detail: msg });
      }
    });
  }

  toggle(item: Cliente) {
    this.confirmationService.confirm({
      message: `¿${item.isActive ? 'Desactivar' : 'Activar'} a ${item.nombreCliente}?`,
      header: 'Confirmar Estado',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.service.toggle(item.id).subscribe(() => {
          const index = this.clientes.findIndex(row => row.id === item.id);
          if (index >= 0) {
            const next = this.clientes.slice();
            next[index] = { ...next[index], isActive: !item.isActive };
            this.clientes = next;
          }
          this.loadKpis();
          this.cdr.detectChanges();
        });
      }
    });
  }

  canInvite(item: Cliente): boolean {
    return item.cuentaEstado !== 'ACTIVA'
      && !!item.email?.trim()
      && item.isActive !== false;
  }

  inviteOne(item: Cliente) {
    if (!this.canInvite(item)) return;
    this.confirmationService.confirm({
      message: `¿Enviar invitación de registro a ${item.email}?`,
      header: 'Invitar cliente',
      icon: 'pi pi-envelope',
      accept: () => {
        this.inviting = true;
        this.service.invitar(item.id).subscribe({
          next: (saved) => {
            this.inviting = false;
            this.messageService.add({
              severity: 'success',
              summary: 'Invitación enviada',
              detail: `Correo enviado a ${item.email}`
            });
            if (saved?.id) this.replaceCliente(saved);
            else this.load({ first: this.first, rows: this.rows });
            this.loadKpis();
          },
          error: (err) => {
            this.inviting = false;
            const msg = err.error?.message || 'No se pudo enviar la invitación.';
            this.messageService.add({ severity: 'error', summary: 'Error', detail: msg });
          }
        });
      }
    });
  }

  inviteAll() {
    this.confirmationService.confirm({
      message: 'Se invitará por correo a todos los clientes sin cuenta web que tengan email. ¿Continuar?',
      header: 'Invitar a todos',
      icon: 'pi pi-send',
      accept: () => {
        this.inviting = true;
        this.service.invitarLote().subscribe({
          next: (res) => {
            this.inviting = false;
            this.messageService.add({
              severity: 'success',
              summary: 'Invitaciones procesadas',
              detail: `Enviados: ${res.enviados}. Omitidos: ${res.omitidos}. Fallidos: ${res.fallidos}.`
            });
            this.load({ first: this.first, rows: this.rows });
            this.loadKpis();
          },
          error: () => {
            this.inviting = false;
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'No se pudo procesar el lote de invitaciones.'
            });
          }
        });
      }
    });
  }

  cuentaLabel(estado?: string): string {
    switch (estado) {
      case 'ACTIVA': return 'Activa';
      case 'INVITADA': return 'Invitada';
      default: return 'Sin cuenta';
    }
  }

  cuentaClass(estado?: string): string {
    switch (estado) {
      case 'ACTIVA': return 'bg-green-100 text-green-700';
      case 'INVITADA': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-700';
    }
  }

  direccionResumen(item: Cliente): string {
    const parts = [item.distritoNombre, item.departamentoNombre].filter(Boolean);
    return parts.length ? parts.join(', ') : '—';
  }
}
